/**
 * Communities realtime — dynamic per-channel + per-community Momento
 * subscriptions. Reuses the shared chat token (same cache, same prefix)
 * but subscribes to the topics published by the community lambdas:
 *
 *   slumbr.community.{cid}.channel.{chid}  → message events
 *   slumbr.community.{cid}                  → community-wide activity
 *
 * The hook is mounted at the communities layout. It watches the
 * store's `activeCommunityId` + `activeChannelId` and rotates
 * subscriptions to match — only one channel is subscribed at a time,
 * keeping topic-count low.
 */

import { useEffect, useRef } from "react";
import {
  TopicClient,
  Configurations,
  CredentialProvider,
  TopicSubscribe,
  type TopicItem,
} from "@gomomento/sdk-web";
import {
  apiClient,
  getRealtimeTransport,
  rtEnabled,
  type FrameMeta,
  type MessageResponse,
} from "@slumbr/core";

import { useCommunitiesStore } from "./stores/communities-store";

interface RealtimeTokenResponse {
  apiKey: string;
  endpoint: string;
  cacheName: string;
  topics: string[];
}

type TokenScope = {
  communityIds: string[];
  channelKeys: string[]; // "cid::chid" pairs
};

interface CachedToken extends RealtimeTokenResponse {
  fetchedAt: number;
  scope: TokenScope;
}

let tokenPromise: Promise<CachedToken> | null = null;
let tokenScope: TokenScope = { communityIds: [], channelKeys: [] };

function channelKey(communityId: string, channelId: string): string {
  return `${communityId}::${channelId}`;
}

function mergeScope(base: TokenScope, extra: TokenScope): TokenScope {
  return {
    communityIds: [...new Set([...base.communityIds, ...extra.communityIds])],
    channelKeys: [...new Set([...base.channelKeys, ...extra.channelKeys])],
  };
}

function scopeCovers(have: TokenScope, want: TokenScope): boolean {
  return (
    want.communityIds.every((id) => have.communityIds.includes(id)) &&
    want.channelKeys.every((key) => have.channelKeys.includes(key))
  );
}

function fetchToken(want: TokenScope): Promise<CachedToken> {
  // Cached token already covers what we need + still fresh → reuse.
  if (tokenPromise && scopeCovers(tokenScope, want)) {
    return tokenPromise;
  }
  const next = mergeScope(tokenScope, want);
  tokenScope = next;
  const communityChannels = next.channelKeys.map((key) => {
    const [communityId, channelId] = key.split("::");
    return { communityId, channelId };
  });
  tokenPromise = apiClient
    .post<RealtimeTokenResponse>("/api/chat/token", {
      communityIds: next.communityIds,
      communityChannels,
    })
    .then((data) => ({ ...data, fetchedAt: Date.now(), scope: next }))
    .catch((err) => {
      tokenPromise = null;
      tokenScope = { communityIds: [], channelKeys: [] };
      throw err;
    });
  return tokenPromise;
}

let cachedClient: { client: TopicClient; cacheName: string; scope: TokenScope } | null = null;

async function getTopicClient(want: TokenScope): Promise<{ client: TopicClient; cacheName: string }> {
  if (cachedClient && scopeCovers(cachedClient.scope, want)) {
    return { client: cachedClient.client, cacheName: cachedClient.cacheName };
  }
  // Re-mint so the new grant set is included.
  cachedClient = null;
  const token = await fetchToken(want);
  if (Date.now() - token.fetchedAt > 50 * 60 * 1000) {
    tokenPromise = null;
    return getTopicClient(want);
  }
  // The /api/chat/token endpoint mints a disposable Momento token —
  // its self-describing JWT format is `fromString()`, not the v2
  // structured key used by static "console" keys. The messenger uses
  // the same `fromString` path; matching it here avoids the
  // "Received an invalid v2 API key" subscribe failure.
  const client = new TopicClient({
    credentialProvider: CredentialProvider.fromString(token.apiKey),
    configuration: Configurations.Laptop.latest(),
  });
  cachedClient = { client, cacheName: token.cacheName, scope: token.scope };
  return { client, cacheName: token.cacheName };
}

/** Topic name helpers — must match what the server publishes. */
function communityChannelTopic(cid: string, chid: string): string {
  // The serverless side uses `${MOMENTO_TOPICS_PREFIX}.community.{cid}.channel.{chid}`.
  // The prefix is `slumbr` in prod — keep this in sync with the
  // Config.MOMENTO_TOPICS_PREFIX parameter.
  return `slumbr.community.${cid}.channel.${chid}`;
}
function communityActivityTopic(cid: string): string {
  return `slumbr.community.${cid}`;
}

interface ChannelMessageEvent {
  type: "community.message.created" | "community.message.updated";
  communityId: string;
  channelId: string;
  message: MessageResponse;
  senderId: string;
}

interface ChannelDeleteEvent {
  type: "community.message.deleted";
  communityId: string;
  channelId: string;
  messageId: string;
}

interface CommunityActivityEvent {
  type: "community.activity";
  communityId: string;
  channelId: string;
  messageId: string;
  senderId: string;
}

type CommunityRealtimeEvent =
  | ChannelMessageEvent
  | ChannelDeleteEvent
  | CommunityActivityEvent;

/** Apply a channel realtime event to the store. Shared by the Momento and the
 *  Durable Object subscription paths so behaviour is identical either way. */
function applyChannelEvent(event: CommunityRealtimeEvent): void {
  const store = useCommunitiesStore.getState();
  if (event.type === "community.message.created") {
    store.ingestMessage(event.channelId, event.message);
  } else if (event.type === "community.message.updated") {
    store.patchMessage(event.channelId, event.message.id, event.message);
  } else if (event.type === "community.message.deleted") {
    store.patchMessage(event.channelId, event.messageId, null);
  }
}

/** DO channel-room frame handler. Every `community.*` event is server-authored
 *  (broadcastRealtime); the channel DO also relays client publishes, stamped
 *  `srvFrom`, so any channel member could otherwise spoof, hide, or rewrite
 *  messages on peers' screens. Communities publishes nothing peer-to-peer over
 *  the DO (typing rides Momento), so all peer frames are dropped. */
export function handleCommunityChannelFrame(raw: unknown, meta: FrameMeta): void {
  if (meta.fromPeer) return;
  const event = raw as CommunityRealtimeEvent;
  if (event && typeof event.type === "string" && event.type.startsWith("community.")) {
    applyChannelEvent(event);
  }
}

function parseItem(item: TopicItem): CommunityRealtimeEvent | null {
  try {
    return JSON.parse(item.valueString()) as CommunityRealtimeEvent;
  } catch {
    return null;
  }
}

/** Hook called once at the communities layout level. Rotates the
 *  active channel + community subscriptions whenever the store's
 *  active ids change. */
export function useCommunityRealtime() {
  const activeCommunityId = useCommunitiesStore((s) => s.activeCommunityId);
  const activeChannelId = useCommunitiesStore((s) => s.activeChannelId);

  // Stash the current subs in refs so the cleanup closure can read
  // them without forcing a new effect run on every render.
  const channelSubRef = useRef<{
    topic: string;
    sub: TopicSubscribe.Subscription;
  } | null>(null);
  const communitySubRef = useRef<{
    topic: string;
    sub: TopicSubscribe.Subscription;
  } | null>(null);

  // Channel-level subscription (messages, edits, deletes).
  useEffect(() => {
    if (!activeCommunityId || !activeChannelId) return;

    // Durable Object path (flag: VITE_RT_COMMUNITIES). One WS to the channel
    // room; the backend fans events out via broadcastRealtime. Read-only
    // subscriber, same as the Momento model below.
    if (rtEnabled("communities")) {
      // Composite room id carries communityId so the token endpoint can check
      // membership; must match the backend broadcast target.
      const channel = getRealtimeTransport().join(
        `community/${activeCommunityId}:${activeChannelId}`
      );
      const off = channel.on(handleCommunityChannelFrame);
      return () => {
        off();
        channel.close();
      };
    }

    const topic = communityChannelTopic(activeCommunityId, activeChannelId);
    let cancelled = false;
    let subscription: TopicSubscribe.Subscription | null = null;

    (async () => {
      try {
        const { client, cacheName } = await getTopicClient({
          communityIds: [activeCommunityId],
          channelKeys: [channelKey(activeCommunityId, activeChannelId)],
        });
        if (cancelled) return;
        const result = await client.subscribe(cacheName, topic, {
          onItem: (item) => {
            const event = parseItem(item);
            if (event) applyChannelEvent(event);
          },
          onError: (err) => {
            console.warn("[CommunitiesRealtime] channel sub error", String(err));
          },
        });
        if (result instanceof TopicSubscribe.Error) {
          console.warn(
            "[CommunitiesRealtime] subscribe failed",
            result.errorCode(),
            result.toString()
          );
          return;
        }
        subscription = result;
        channelSubRef.current = { topic, sub: result };
      } catch (err) {
        console.warn("[CommunitiesRealtime] channel sub setup failed", err);
      }
    })();

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
      if (channelSubRef.current?.topic === topic) {
        channelSubRef.current = null;
      }
    };
  }, [activeCommunityId, activeChannelId]);

  // Community-wide subscription (activity for sidebar badges).
  useEffect(() => {
    if (!activeCommunityId) return;
    const topic = communityActivityTopic(activeCommunityId);
    let cancelled = false;
    let subscription: TopicSubscribe.Subscription | null = null;

    (async () => {
      try {
        const { client, cacheName } = await getTopicClient({
          communityIds: [activeCommunityId],
          channelKeys: [],
        });
        if (cancelled) return;
        const result = await client.subscribe(cacheName, topic, {
          onItem: (item) => {
            const event = parseItem(item);
            if (!event) return;
            // For now, community activity events only refresh the
            // member list when activity hints at new members. Other
            // events are handled by the channel subscription.
          },
          onError: (err) => {
            console.warn(
              "[CommunitiesRealtime] community sub error",
              String(err)
            );
          },
        });
        if (result instanceof TopicSubscribe.Error) return;
        subscription = result;
        communitySubRef.current = { topic, sub: result };
      } catch (err) {
        console.warn(
          "[CommunitiesRealtime] community sub setup failed",
          err
        );
      }
    })();

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
      if (communitySubRef.current?.topic === topic) {
        communitySubRef.current = null;
      }
    };
  }, [activeCommunityId]);
}
